import { FC, ReactNode, isValidElement } from "react"
import { Fade } from "@mui/material"
import MUITooltip, {
   TooltipProps as MUITooltipProps,
} from "@mui/material/Tooltip"

export enum TooltipPosition {
   TOP = "top",
   RIGHT = "right",
   BOTTOM = "bottom",
   LEFT = "left",
}

export interface TooltipProps {
   content: ReactNode
   children: MUITooltipProps["children"]
   open?: boolean
   position?: TooltipPosition
   enterDelay?: number
   classes?: MUITooltipProps["classes"]
   wrap?: boolean
}

const Tooltip: FC<TooltipProps> = ({
   content,
   children,
   open,
   position = TooltipPosition.TOP,
   enterDelay = 100,
   classes,
   wrap = false,
}) => {
   const colors = {
      gunMetal: "#242F38",
      white: "#fff",
   }

   const tooltipStyleBase = {
      fontWeight: 400,
      fontFamily: "Rubik",
      fontSize: "12px",
      fontStyle: "normal",
      borderRadius: "4px",
      padding: "8px",
      lineHeight: "16px",
      whiteSpace: "nowrap",
      maxWidth: "none",
      ...(wrap && {
         whiteSpace: "normal",
         maxWidth: "580px",
         wordBreak: "break-word",
      }),
   }

   const childElement = isValidElement(children) ? children : null
   const isNativeElement = childElement && typeof childElement.type === "string"

   const isControlled = open !== undefined

   return (
      <MUITooltip
         title={content}
         placement={position}
         arrow
         {...(isControlled && { open })}
         disableHoverListener={isControlled}
         enterDelay={enterDelay}
         slots={{
            transition: Fade,
         }}
         slotProps={{
            transition: { timeout: 0 },
            tooltip: {
               sx: {
                  backgroundColor: colors.white,
                  color: colors.gunMetal,
                  filter: "drop-shadow(0 0 8px rgb(0 0 0 / 30%))",
                  ...tooltipStyleBase,
               },
            },
            arrow: {
               sx: {
                  color: colors.white,
                  fontSize: "18px",
               },
            },
         }}
         classes={classes}
      >
         {isNativeElement ? children : <span>{children}</span>}
      </MUITooltip>
   )
}

export default Tooltip
